import { useEffect, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Info, X } from 'lucide-react'
import { api, type AnalyticsAppReport, type AnalyticsExitScreen, type AnalyticsFunnel, type AnalyticsQualityRow, type AnalyticsRetentionPoint, type AnalyticsScreenRow } from '@/api/client'
import { statusOf } from '@/lib/status'
import { Button, Code, DataTable, Disclosure, EmptyState, ErrorState, Notice, Skeleton, StatTile, TonePill, type Column } from '@/ui'
import { DayDelta, UsageTrend } from './charts'
import { fmtDuration, fmtNum, fmtPct, freshnessText, hourLabel, UNPAIRED_WARN, utcHourText } from './format'
import { WindowPicker } from './pickers'
import { QualityCard } from './QualityCard'
import { Panel, Section } from './Section'

/** The cache entry of one app's report. The header of the detail page reads
 *  the same entry as the panel (no drill-down), so it costs no extra request
 *  and follows the chosen window. */
export function useAnalyticsAppReport(appId: string, days: number, platform: string, hour: number | null = null, day = '', at = '') {
  return useQuery({
    queryKey: ['analytics-app', appId, days, platform, hour, day, at],
    queryFn: () => api.analyticsApp(appId, days, platform, hour, day, at),
    enabled: !!appId,
    // Changing the window or the platform is a new key; without this the page
    // blanks until it lands, and takes the pickers' own options with it.
    placeholderData: keepPreviousData,
  })
}

/** How current the data is: the newest event the collector has pulled, not the
 *  clock on the wall. Collection is a five-minute poll. */
export function Freshness({ lastEventAt }: { lastEventAt: number }) {
  if (!lastEventAt) return null
  return (
    <span className="text-meta text-muted-foreground" title="수집기가 가져온 가장 최근 이벤트. 5분마다 가져온다.">
      마지막 이벤트 <span className="font-medium text-foreground">{freshnessText(lastEventAt)}</span>
    </span>
  )
}

/** One app's first-party telemetry.
 *
 *  `appId` is the BUNDLE id the app reports (com.pentarab.constella), not the
 *  ASO row id — it is what the client sends and what joins the two datasets.
 *  Drawn on `/analytics/:appId` and as the 사용량 tab of an app, which is the
 *  point: keyword rank and install conversion on one screen. */
export function AnalyticsPanel({
  appId,
  days: daysProp,
  onDaysChange,
  platform = '',
}: {
  appId: string
  /** Controlled window. Omitted it keeps its own. */
  days?: number
  onDaysChange?: (d: number) => void
  /** Scopes every figure below, not just which app is shown. This panel runs
   *  its own query: a page that filtered only its header would print a
   *  filtered headline over unfiltered charts, which is worse than not
   *  filtering at all because the two disagree and neither says so. */
  platform?: string
}) {
  const [ownDays, setOwnDays] = useState(30)
  const days = daysProp ?? ownDays
  const setDays = onDaysChange ?? setOwnDays
  // Which hour of the day everything below the chart answers for. Local to the
  // panel rather than the URL: it only means anything while the hourly chart
  // is on screen, and it is cleared whenever the window changes.
  const [hour, setHour] = useState<number | null>(null)
  // Which day of a multi-day window everything answers for. Picking one
  // collapses the window onto it, which is also what turns the chart hourly.
  const [day, setDay] = useState('')
  // The 24-hour window picks an hour by instant, not by hour-of-day: it spans
  // two UTC dates, so "14" would be ambiguous between them.
  const [at, setAt] = useState('')
  useEffect(() => {
    setHour(null)
    setDay('')
    setAt('')
  }, [days])
  const q = useAnalyticsAppReport(appId, days, platform, hour, day, at)
  const rep = q.data

  if (!appId) {
    return (
      <EmptyState
        icon={Info}
        title="이 앱에는 번들 ID가 없다"
        description="사용량은 번들 ID로 앱과 이어진다. ASO 앱 기록에 번들 ID를 넣으면 연결된다."
      />
    )
  }

  const worstQuality = (rep?.quality ?? []).reduce<AnalyticsQualityRow | null>(
    (worst, row) => (row.enters > 0 && (!worst || row.unpaired_enter_rate > worst.unpaired_enter_rate) ? row : worst),
    null,
  )
  const hourly = rep?.funnel_hourly
  const t = rep?.totals

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="min-w-0 space-y-0.5 text-table text-muted-foreground">
          {rep ? (
            <div>
              <span title="날짜는 UTC다. UTC 하루는 서울 09:00에 넘어간다.">
                {rep.from} → {rep.to} UTC
              </span>{' '}
              · <Code>{rep.app_id}</Code>
            </div>
          ) : (
            <Skeleton className="h-5 w-56" />
          )}
          <Freshness lastEventAt={t?.last_event_at ?? 0} />
        </div>
        <WindowPicker days={days} onChange={setDays} />
      </div>

      {q.isError && <ErrorState title="사용량을 불러오지 못했다" error={q.error} onRetry={() => q.refetch()} />}

      {rep && rep.totals.sessions === 0 && (
        <EmptyState
          title="이 기간에 기록이 없다"
          description="앱에 분석 전송 코드가 아직 없거나, 수집기 설정이 안 됐다. 설정의 자체 분석 수집기를 확인한다."
        />
      )}

      {/* The metric-rot alarm, shown before any number it invalidates. */}
      {worstQuality && worstQuality.unpaired_enter_rate >= UNPAIRED_WARN && <QualityWarning row={worstQuality} />}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatTile label="세션" value={fmtNum(t?.sessions ?? 0)} />
        <StatTile label="사용자" value={fmtNum(t?.users ?? 0)} />
        <StatTile label="신규 사용자" value={fmtNum(t?.new_users ?? 0)} hint="기간 안 first_open" />
        <StatTile label="평균 세션" value={fmtDuration(t?.avg_session_ms ?? 0)} />
        <StatTile label="화면 조회" value={fmtNum(t?.screen_enters ?? 0)} />
        <StatTile label="측정한 체류" value={fmtDuration(t?.total_dwell_ms ?? 0)} hint="짝이 맞는 구간만" />
      </div>

      <Section
        title={hourly ? '시간별 추이' : '일별 추이'}
        aside="UTC 하루는 서울 09:00에 넘어간다"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {day !== '' && (
              <Button
                size="sm"
                icon={X}
                title="기간 전체로 돌아간다"
                onClick={() => {
                  setDay('')
                  setHour(null)
                }}
              >
                {day}
              </Button>
            )}
            {hour !== null && (
              <Button size="sm" icon={X} title="하루 전체로 돌아간다" onClick={() => setHour(null)}>
                {utcHourText(hour)}
              </Button>
            )}
            {at !== '' && (
              <Button size="sm" icon={X} title="24시간 전체로 돌아간다" onClick={() => setAt('')}>
                {hourLabel(at)}
              </Button>
            )}
            {!hourly && <DayDelta series={rep?.funnel_daily ?? []} />}
          </div>
        }
      >
        <Panel>
          {q.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            // The server decides which series this is: a one-day window —
            // chosen with the picker or by clicking a bar — comes back with 24
            // hour buckets, anything longer with days.
            <UsageTrend
              series={
                hourly
                  ? hourly.map((h) => ({
                      ...h,
                      // Both shapes become an instant so one label function can
                      // put the Seoul clock beside every bar. A single-day
                      // window numbers its buckets 00..23 and the date comes
                      // from the window; the 24-hour one already sends the
                      // instant, because its buckets span two dates.
                      day: h.hour.includes('T') ? h.hour : `${rep!.from}T${h.hour}:00:00Z`,
                    }))
                  : (rep?.funnel_daily ?? [])
              }
              onPick={
                hourly
                  ? (i) => {
                      const bucket = hourly[i]
                      if (!bucket) return
                      if (bucket.hour.includes('T')) setAt((cur) => (cur === bucket.hour ? '' : bucket.hour))
                      else setHour((cur) => (cur === i ? null : i))
                    }
                  : (i) => {
                      const picked = rep?.funnel_daily?.[i]?.day
                      if (picked) setDay(picked)
                    }
              }
              selected={hourly ? (at ? hourly.findIndex((h) => h.hour === at) : hour) : null}
            />
          )}
        </Panel>
      </Section>

      <ScreensTable rows={rep?.screens ?? []} />

      <div className="grid gap-6 lg:grid-cols-2">
        <FunnelSection rep={rep} />
        <RetentionSection retention={rep?.retention ?? []} hourPicked={hour !== null} />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <BreakdownTable
          title="국가"
          rows={(rep?.countries ?? []).map((c) => ({
            key: c.country || '(알 수 없음)',
            sessions: c.sessions,
            users: c.users,
            extra: fmtDuration(c.avg_session_ms),
          }))}
          extraLabel="평균 세션"
        />
        <BreakdownTable
          title="기기"
          rows={(rep?.devices ?? []).map((d) => ({
            key: [d.os, d.os_version, d.model].filter(Boolean).join(' ') || '(알 수 없음)',
            sessions: d.sessions,
            users: d.users,
          }))}
        />
        <BreakdownTable
          title="로케일"
          rows={(rep?.locales ?? []).map((l) => ({ key: l.locale || '(알 수 없음)', sessions: l.sessions, users: l.users }))}
        />
      </div>

      <ExitScreens rows={rep?.exit_screens ?? []} />
      <QualityCard rows={rep?.quality ?? []} />

      <PanelNotes />
    </div>
  )
}

function QualityWarning({ row }: { row: AnalyticsQualityRow }) {
  return (
    <Notice
      role="alert"
      tone={statusOf('quality', 'warn').tone}
      title={`${row.platform} ${row.app_version}의 화면 진입 ${fmtPct(row.unpaired_enter_rate)}에 screen_exit이 없다`}
    >
      이 버전의 체류 시간은 짝이 맞은 일부 방문으로만 계산된다. 앱이 화면이 사라질 때, 모달을 열 때, 백그라운드로 갈 때 screen_exit을 보내기 전까지는 믿지 않는다.
    </Notice>
  )
}

function ScreensTable({ rows }: { rows: AnalyticsScreenRow[] }) {
  const columns: Column<AnalyticsScreenRow>[] = [
    {
      key: 'screen',
      header: '화면',
      card: 'title',
      className: 'font-medium',
      cell: (s) => s.screen || <span className="text-muted-foreground">(screen 값 없음)</span>,
    },
    {
      key: 'views',
      header: '조회',
      align: 'right',
      className: 'tabular-nums',
      card: 'line',
      cell: (s) => (
        <>
          {fmtNum(s.views)}
          {s.discarded_short > 0 && (
            <span
              className="ml-1 text-meta text-muted-foreground"
              title={`시트 전환 ${fmtNum(s.discarded_short)}건이 이 화면을 아무도 보지 못한 채 지나갔고, 조회에 넣지 않았다`}
            >
              +{fmtNum(s.discarded_short)}
            </span>
          )}
        </>
      ),
    },
    { key: 'users', header: '사용자', align: 'right', className: 'tabular-nums', card: 'hidden', cell: (s) => fmtNum(s.users) },
    { key: 'sessions', header: '세션', align: 'right', className: 'tabular-nums', card: 'hidden', cell: (s) => fmtNum(s.sessions) },
    {
      key: 'avg',
      header: '평균 체류',
      align: 'right',
      className: 'tabular-nums',
      card: 'hidden',
      cell: (s) => (s.counted > 0 ? fmtDuration(s.avg_dwell_ms) : '—'),
    },
    { key: 'total', header: '총 체류', align: 'right', className: 'tabular-nums', card: 'hidden', cell: (s) => fmtDuration(s.dwell_ms_total) },
    // the phone pairs what is read together; the two checks show only when
    // they have something
    {
      key: 'people',
      header: '사용자 · 세션',
      cardOnly: true,
      cell: (s) => (
        <span className="tabular-nums">
          {fmtNum(s.users)} <span className="text-muted-foreground">·</span> {fmtNum(s.sessions)}
        </span>
      ),
    },
    {
      key: 'dwell',
      header: '평균 · 총 체류',
      cardOnly: true,
      cell: (s) => (
        <span className="tabular-nums">
          {s.counted > 0 ? fmtDuration(s.avg_dwell_ms) : '—'} <span className="text-muted-foreground">·</span> {fmtDuration(s.dwell_ms_total)}
        </span>
      ),
    },
    {
      key: 'unpaired',
      header: <span title="종료가 짝으로 붙지 않은 진입. 조회에는 들어가지만 시간은 0이다.">짝 없음</span>,
      align: 'right',
      className: 'tabular-nums',
      card: 'line',
      cardHide: (s) => s.unpaired_enters === 0,
      cell: (s) =>
        s.unpaired_enters > 0 ? (
          s.unpaired_enter_rate >= UNPAIRED_WARN ? (
            <TonePill tone={statusOf('quality', 'warn').tone} label={`${fmtNum(s.unpaired_enters)} · ${fmtPct(s.unpaired_enter_rate, 0)}`} />
          ) : (
            <span className="text-muted-foreground">
              {fmtNum(s.unpaired_enters)} · {fmtPct(s.unpaired_enter_rate, 0)}
            </span>
          )
        ) : (
          <span className="text-muted-foreground">0</span>
        ),
    },
    {
      key: 'dropped',
      header: <span title="음수 체류 + 24시간 넘는 체류 + 짝 없는 종료">버려짐</span>,
      align: 'right',
      className: 'tabular-nums',
      card: 'line',
      cardHide: (s) => s.discarded_neg + s.discarded_long + s.orphan_exits === 0,
      cell: (s) => {
        const dropped = s.discarded_neg + s.discarded_long + s.orphan_exits
        return dropped > 0 ? (
          <span title="음수 체류 + 24시간 넘는 체류 + 짝 없는 종료">{fmtNum(dropped)}</span>
        ) : (
          <span className="text-muted-foreground">0</span>
        )
      },
    },
  ]
  return (
    <Section title="화면" aside="최소 체류 시간보다 짧은 방문은 조회에서 빼고 +N으로 따로 보인다">
      <DataTable label="화면" rows={rows} rowKey={(s) => s.screen} columns={columns} empty={<EmptyState title="아직 화면 이벤트가 없다" />} />
    </Section>
  )
}

function FunnelSection({ rep }: { rep?: Pick<AnalyticsAppReport, 'funnel' | 'funnel_by_reason'> }) {
  const f = rep?.funnel
  const reasons = rep?.funnel_by_reason ?? []
  const columns: Column<AnalyticsFunnel>[] = [
    { key: 'reason', header: '사유', card: 'title', className: 'font-mono text-meta', cell: (r) => r.reason || '(없음)' },
    { key: 'shown', header: '노출', align: 'right', className: 'tabular-nums', card: 'hidden', cell: (r) => fmtNum(r.paywall_shown) },
    { key: 'started', header: '시작', align: 'right', className: 'tabular-nums', card: 'hidden', cell: (r) => fmtNum(r.purchase_started) },
    { key: 'bought', header: '구매', align: 'right', className: 'tabular-nums', card: 'hidden', cell: (r) => fmtNum(r.purchase_success) },
    {
      // the phone reads the funnel as one line, left to right
      key: 'steps',
      header: '노출 · 시작 · 구매',
      cardOnly: true,
      cell: (r) => (
        <span className="tabular-nums">
          {fmtNum(r.paywall_shown)} <span className="text-muted-foreground">·</span> {fmtNum(r.purchase_started)}{' '}
          <span className="text-muted-foreground">·</span> {fmtNum(r.purchase_success)}
        </span>
      ),
    },
    { key: 'cancelled', header: '취소', align: 'right', className: 'tabular-nums', card: 'line', cardHide: (r) => r.purchase_cancelled === 0, cell: (r) => fmtNum(r.purchase_cancelled) },
    { key: 'conv', header: '전환', align: 'right', className: 'tabular-nums', card: 'meta', cell: (r) => fmtPct(r.conversion_rate, 1) },
  ]
  return (
    <Section title="페이월 퍼널" aside="페이월이 열린 사유가 체류 시간보다 전환을 더 잘 설명한다">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4">
          <StatTile label="페이월 노출" value={fmtNum(f?.paywall_shown ?? 0)} />
          <StatTile label="페이월 도달" value={fmtPct(f?.paywall_rate ?? 0, 0)} hint="사용자 중" />
          <StatTile label="구매 시작" value={fmtNum(f?.purchase_started ?? 0)} />
          <StatTile label="구매" value={fmtNum(f?.purchase_success ?? 0)} hint={f ? `노출 대비 ${fmtPct(f.conversion_rate, 1)}` : undefined} />
        </div>
        <DataTable
          label="페이월 사유"
          rows={reasons}
          rowKey={(r) => r.reason}
          columns={columns}
          empty={<EmptyState title="아직 페이월 이벤트가 없다" />}
        />
      </div>
    </Section>
  )
}

const RETENTION_MARKS = [0, 1, 3, 7, 14, 30]

function RetentionSection({ retention, hourPicked }: { retention: AnalyticsRetentionPoint[]; hourPicked: boolean }) {
  const byOffset = new Map(retention.map((p) => [p.day_offset, p]))
  const empty = RETENTION_MARKS.every((d) => !byOffset.has(d))
  return (
    <Section title="리텐션" aside="first_open 코호트">
      <Panel className="space-y-3">
        {empty && <p className="text-table text-muted-foreground">쌓인 이력이 아직 모자란다.</p>}
        {RETENTION_MARKS.map((d) => {
          const p = byOffset.get(d)
          if (!p) return null
          return (
            <div key={d} className="flex items-center gap-3">
              <span className="w-10 shrink-0 text-table font-medium text-muted-foreground">D{d}</span>
              <div role="img" aria-label={`D${d} ${fmtPct(p.rate, 0)}`} className="h-2.5 flex-1 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-chart-1" style={{ width: `${Math.min(100, p.rate * 100)}%` }} />
              </div>
              <span className="w-28 shrink-0 text-right text-table tabular-nums">
                {fmtPct(p.rate, 0)}{' '}
                <span className="text-meta text-muted-foreground">
                  {fmtNum(p.retained)}/{fmtNum(p.cohort_size)}
                </span>
              </span>
            </div>
          )
        })}
        {hourPicked && (
          <p className="text-meta text-muted-foreground">고른 시간은 이 표에 적용되지 않는다. D7 코호트는 날짜 단위라 시간으로 좁힐 수 없다.</p>
        )}
      </Panel>
    </Section>
  )
}

interface BreakdownRow {
  key: string
  sessions: number
  users: number
  extra?: string
}

function BreakdownTable({ title, rows, extraLabel }: { title: string; rows: BreakdownRow[]; extraLabel?: string }) {
  const columns: Column<BreakdownRow>[] = [
    { key: 'key', header: title, card: 'title', className: 'max-w-[14rem] truncate', cell: (r) => r.key },
    { key: 'sessions', header: '세션', align: 'right', className: 'tabular-nums', card: 'line', cell: (r) => fmtNum(r.sessions) },
    { key: 'users', header: '사용자', align: 'right', className: 'tabular-nums', card: 'line', cell: (r) => fmtNum(r.users) },
    ...(extraLabel
      ? [{ key: 'extra', header: extraLabel, align: 'right' as const, className: 'tabular-nums', card: 'line' as const, cell: (r: BreakdownRow) => r.extra }]
      : []),
  ]
  return (
    <Section title={title}>
      <DataTable
        label={title}
        rows={rows.slice(0, 12)}
        rowKey={(r) => r.key}
        columns={columns}
        empty={<EmptyState title="데이터가 없다" />}
      />
    </Section>
  )
}

function ExitScreens({ rows }: { rows: AnalyticsExitScreen[] }) {
  if (rows.length === 0) return null
  return (
    <Section title="세션이 끝나는 화면" aside="세션이 닫히기 전 마지막으로 들어간 화면, 곧 이탈 지점">
      <Panel className="space-y-2">
        {rows.slice(0, 10).map((r) => (
          <div key={r.screen} className="flex items-center gap-3">
            <span className="w-32 shrink-0 truncate text-table sm:w-40">{r.screen}</span>
            <div role="img" aria-label={`${r.screen} ${fmtPct(r.share, 0)}`} className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-chart-1" style={{ width: `${Math.min(100, r.share * 100)}%` }} />
            </div>
            <span className="w-12 shrink-0 text-right text-table tabular-nums">{fmtPct(r.share, 0)}</span>
          </div>
        ))}
      </Panel>
    </Section>
  )
}

/** The paragraphs that used to sit under every heading, said once. */
function PanelNotes() {
  return (
    <Disclosure title="설명">
      <dl className="space-y-3 text-table">
        <Note term="추이">
          막대 하나가 UTC 하루(24시간 창에서는 한 시간)다. 막대를 누르면 그 날을 시간별로 열고, 시간을 누르면 아래 전체가 그 시간만 답한다. 데이터가 없는 구간도 0으로 그려서
          간격이 실제 시간과 같다. 세션과 사용자는 위에, 페이월 노출과 구매는 아래에 그린다. UTC 하루는 서울 09:00에 넘어가므로 시간 막대에는 KST를 같이 적는다.
        </Note>
        <Note term="화면">
          조회는 믿을 만한 최소 체류보다 짧은 방문을 뺀 수다. 시트를 바꾸면 아래 화면에 들어왔다 같은 프레임에 나가는데, 그것은 회색 +N으로 따로 보인다. 체류는 짝이 맞는
          screen_enter → screen_exit 구간의 합이다. 종료가 없는 진입은 조회에는 들어가지만 시간은 더하지 않고 &ldquo;짝 없음&rdquo;에 센다.
        </Note>
        <Note term="리텐션">
          first_open으로 코호트를 만든다. 코호트는 N일이 지난 뒤에야 D-N의 분모에 들어가므로 새 설치가 D7을 희석하지 않는다.
        </Note>
        <Note term="국가·기기·로케일">
          국가는 요청 IP에서 엣지가 붙이고 IP 자체는 저장하지 않는다. 기기는 OS, 버전, 모델이며 광고 ID와 기기 지문은 없다. 로케일은 앱 언어다. 미국의 한국어 사용자는 미국
          사용자와 다른 질문이다.
        </Note>
        <Note term="데이터 품질">
          앱 버전별 짝 비율, 중복 비율, 버려진 이벤트다. 깨진 릴리스가 석 달치 체류 시간을 망가뜨리고도 모른 채 지나가지 않도록 이 표가 먼저 알린다.
        </Note>
      </dl>
    </Disclosure>
  )
}

function Note({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="font-medium">{term}</dt>
      <dd className="mt-0.5 text-muted-foreground">{children}</dd>
    </div>
  )
}
